import React from "react";
import classNames from "classnames";
import Spinner from "@/components/ui/Spinner/Spinner";
import "./OpticksButton.css";

export type OpticksButtonSize = "xs" | "s" | "m" | "m-wide" | "unset";

export interface Props {
  variation?: string;
  size?: OpticksButtonSize;
  display?: string;
  shape?: "default" | "rounded";
  disabled?: boolean;
  loading?: boolean;
  onClick?:
    | React.MouseEventHandler<HTMLButtonElement>
    | ((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void)
    | (() => void);
  type?: "button" | "submit" | "reset";
  label?: string | React.ReactNode;
  icon?: React.ReactNode;
  iconTrailing?: React.ReactNode;
  className?: string;
  "aria-label"?: string;
}

const OpticksButton = ({
  variation,
  display,
  shape,
  className,
  loading = false,
  size,
  disabled,
  type,
  label,
  icon,
  iconTrailing,
  onClick,
  "aria-label": ariaLabel,
}: Props) => {
  const isIconOnly = !!icon && !label && !iconTrailing;
  const resolvedVariation = variation ?? (isIconOnly ? "ghost" : "primary");
  const resolvedSize = size ?? (isIconOnly ? "unset" : "m");
  const resolvedShape = resolvedVariation.startsWith("soft")
    ? shape
    : undefined;
  const isDarkBackground = ["primary", "highlighted", "destructive"].includes(
    resolvedVariation
  );

  return (
    <button
      className={classNames(
        "opticks-button",
        { [`variation-${resolvedVariation}`]: resolvedVariation },
        { [`size-${resolvedSize}`]: resolvedSize },
        { [`display-${display}`]: display },
        { [`shape-${resolvedShape}`]: resolvedShape },
        { "is-loading": loading },
        { [`${className}`]: className && className.length > 0 }
      )}
      type={type || "button"}
      disabled={loading || disabled}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      {loading && (
        <Spinner size={16} color={isDarkBackground ? "mono" : "blue"} />
      )}
      {icon && icon}
      {label && <b>{label}</b>}
      {iconTrailing && iconTrailing}
    </button>
  );
};

export default React.memo(OpticksButton);
